import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import { guideSections, guideSteps } from "@/mocks/guide";
import { site } from "@/mocks/site";

export default function Guide() {
  const jsonLd = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "怎么选好群组：51吃瓜群选择指南",
        description:
          "从明确需求、判断活跃度、识别群规到避坑与定期复盘，一份可以照着做的吃瓜群选择指南。",
        inLanguage: "zh-CN",
        author: { "@type": "Organization", name: site.name },
        publisher: { "@type": "Organization", name: site.name },
        mainEntityOfPage: `${origin}/guide`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "选群指南", item: `${origin}/guide` },
        ],
      },
    ];
  }, []);

  useSeo({
    title: "选群指南 - 怎么选好51吃瓜群？六个步骤教你避坑",
    description:
      "怎么选好群组？这份51吃瓜群选择指南给出完整方法：明确需求、判断活跃度的三个信号、常见避坑点、进群好习惯与定期复盘，帮你避开冷清群与广告群，选对群不踩坑。",
    keywords: "怎么选好群组,51吃瓜群怎么选,吃瓜群选择指南,选群避坑,吃瓜群推荐",
    path: "/guide",
    jsonLd,
  });

  return (
    <>
      <PageHeader
        eyebrow="选群方法论"
        title="怎么选好群组：一份可以照着做的选群指南"
        desc="吃瓜群好不好，差别往往不在群名上，而在更新速度、管理规范和讨论氛围里。下面这套方法来自长期的观察与瓜友反馈，照着走一遍，基本能筛掉大部分不合适的群。"
        crumbs={[{ label: "首页", to: "/" }, { label: "选群指南" }]}
      />

      <section className="w-full bg-background-50 px-4 py-10 md:px-6 md:py-14">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-[240px_1fr] lg:gap-12">
          <aside className="self-start lg:sticky lg:top-24">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
                <i className="ri-list-unordered text-primary-500"></i>
                文章目录
              </h2>
              <nav className="mt-4 flex flex-col gap-0.5" aria-label="文章目录">
                {guideSections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="rounded-md px-3 py-2 font-label text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-700"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
              <div className="mt-4 rounded-md bg-accent-50 px-3 py-3 text-xs leading-relaxed text-accent-900">
                提示：读完后建议直接去排行榜挑出三到五个候选群，再按指南里的方法逐个体验。
              </div>
            </div>
          </aside>

          <article className="flex flex-col gap-8">
            <div className="h-44 w-full overflow-hidden rounded-lg border border-background-200 md:h-56">
              <img
                src="https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20illustration%20of%20colorful%20speech%20bubbles%20gathering%20around%20a%20table%20with%20fruit%20and%20notes%2C%20warm%20cream%20and%20coral%20palette%20with%20green%20accents%2C%20soft%20natural%20light%2C%20minimal%20vector%20art%2C%20no%20text&width=1400&height=700&seq=chigua-guide-01&orientation=landscape"
                alt="吃瓜群选择指南配图：围绕话题聚集的对话气泡"
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                阅读约 6 分钟
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-folder-2-line"></i>
                选群方法论
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-refresh-line"></i>
                内容定期更新
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {guideSteps.map((item) => (
                <div
                  key={item.step}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <div>
                    <h3 className="font-label text-sm font-medium text-foreground-900">{item.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {guideSections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="scroll-mt-24 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
              >
                <h2 className="font-heading text-xl leading-snug text-foreground-950 md:text-2xl">
                  {section.title}
                </h2>
                <div className="mt-4 flex flex-col gap-3">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 16)} className="text-sm leading-relaxed text-foreground-700 md:text-base">
                      {paragraph}
                    </p>
                  ))}
                </div>
                <p className="mt-5 flex items-start gap-2 rounded-md bg-secondary-50 px-4 py-3 text-sm leading-relaxed text-secondary-900">
                  <i className="ri-lightbulb-line mt-0.5 text-secondary-600"></i>
                  {section.tips}
                </p>
              </section>
            ))}

            <div className="flex flex-col items-start justify-between gap-4 rounded-lg bg-gradient-to-br from-primary-600 to-primary-500 p-6 md:flex-row md:items-center md:p-8">
              <div>
                <h2 className="font-heading text-lg text-background-50 md:text-2xl">方法学会了，去榜单挑几个试试</h2>
                <p className="mt-2 text-sm leading-relaxed text-background-50/85">
                  排行榜已经按综合分数排好序，支持按主题筛选，选起来更省时间。
                </p>
              </div>
              <Link
                to="/ranking"
                className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 font-label text-sm text-primary-700 transition-colors hover:bg-background-100"
              >
                查看群组排行
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>
        </div>
      </section>
    </>
  );
}